import React, { useRef, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { motion } from 'framer-motion';

const TestimonialsSection = () => {
  const scrollContainerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const testimonials = [
    {
      id: 1,
      name: "Familie van der Berg",
      location: "Amsterdam, Noord-Holland",
      avatar: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1",
      alt: "Portret van Familie van der Berg, tevreden klanten uit Amsterdam",
      rating: 5,
      service: "Zonnepanelen + GRP Thuisbatterij",
      provider: "GRP Energy",
      testimonial: `Fantastische ervaring! De GRP Wall Battery met 20.000 cycli gegarandeerde levensduur geeft ons echt vertrouwen. We besparen nu €140 per maand op onze energierekening en zijn 85% zelfvoorzienend.`,
      savings: "€140/maand",
      installationTime: "1 dag",
      energyReduction: "85%"
    },
    {
      id: 2,
      name: "Mark en Sandra Jansen",
      location: "Utrecht, Utrecht",
      avatar: "https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1",
      alt: "Foto van Mark en Sandra Jansen, klanten uit Utrecht",
      rating: 5,
      service: "Warmtepomp installatie",
      provider: "WarmtePomp Experts",
      testimonial: `Onze oude CV-ketel was aan vervanging toe. De warmtepomp werkt perfect en ons huis is nu veel comfortabeler. In de winter constant 21°C en onze gasrekening is met 70% gedaald!`,
      savings: "€95/maand",
      installationTime: "2 dagen",
      energyReduction: "70%"
    },
    {
      id: 3,
      name: "Petra Willems",
      location: "Den Haag, Zuid-Holland",
      avatar: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1",
      alt: "Portret van Petra Willems, tevreden klant uit Den Haag",
      rating: 5,
      service: "Complete isolatie + Laadpaal",
      provider: "EcoHome Solutions",
      testimonial: `Als alleenstaande moeder wilde ik mijn huis duurzamer maken. EcoHome heeft mijn hele huis geïsoleerd en een laadpaal geïnstalleerd. Mijn energierekening is gehalveerd!`,
      savings: "€75/maand",
      installationTime: "3 dagen",
      energyReduction: "50%"
    },
    {
      id: 4,
      name: "Jan de Vries",
      location: "Rotterdam, Zuid-Holland",
      avatar: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1",
      alt: "Foto van Jan de Vries, tevreden klant uit Rotterdam",
      rating: 5,
      service: "HR++ Glas",
      provider: "GlasExpert BV",
      testimonial: `Nieuwe ramen geplaatst en het verschil is enorm. Geen tocht meer en veel stiller in huis. De investering verdient zichzelf terug in 8 jaar.`,
      savings: "€60/maand",
      installationTime: "2 dagen",
      energyReduction: "40%"
    },
    {
      id: 5,
      name: "Lisa en Tom Bakker",
      location: "Eindhoven, Noord-Brabant",
      avatar: "https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1",
      alt: "Portret van Lisa en Tom Bakker, klanten uit Eindhoven",
      rating: 5,
      service: "Vloerisolatie",
      provider: "IsolatieProfs",
      testimonial: `Onze vloer was altijd koud. Na isolatie is het verschil dag en nacht. Warme voeten en lagere stookkosten. Top service!`,
      savings: "€50/maand",
      installationTime: "1 dag",
      energyReduction: "35%"
    },
    {
      id: 6,
      name: "Mohammed El Amrani",
      location: "Almere, Flevoland",
      avatar: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=1",
      alt: "Foto van Mohammed El Amrani, tevreden klant uit Almere",
      rating: 5,
      service: "GRP Thuisbatterij 10 kWh",
      provider: "GRP Energy",
      testimonial: `De GRP Wall Battery is een topproduct. Met 20.000 cycli levensduur en de grafeen-technologie weet ik dat dit 20+ jaar meegaat. We besparen nu €95 per maand en zijn nauwelijks nog afhankelijk van het net.`,
      savings: "€95/maand",
      installationTime: "1 dag",
      energyReduction: "70%"
    }
  ];

  // Duplicate testimonials for infinite scroll effect
  const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];

  const overallStats = {
    averageRating: 4.8,
    totalReviews: 780,
    satisfactionRate: 96
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    let animationFrameId;
    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame

    const scroll = () => {
      if (!isPaused && scrollContainer) {
        scrollPosition += scrollSpeed;
        
        // Reset scroll position for infinite loop
        const maxScroll = scrollContainer?.scrollWidth / 3;
        if (scrollPosition >= maxScroll) {
          scrollPosition = 0;
        }
        
        scrollContainer.scrollLeft = scrollPosition;
      }
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isPaused]);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-8 sm:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Star" size={24} className="text-yellow-500 fill-current" />
            <span className="text-sm font-caption font-medium text-yellow-600">
              Reviews & Ervaringen
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Wat Onze Klanten Zeggen
          </h2>
          <p className="text-lg font-body text-muted-foreground max-w-3xl mx-auto">
            Ontdek hoe Nederlandse gezinnen hun energierekening hebben verlaagd en hun woning 
            duurzamer hebben gemaakt met hulp van onze gecertificeerde providers.
          </p>
          
          {/* Overall Rating */}
          <div className="flex items-center justify-center space-x-6 mt-6">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-1">
                <div className="flex items-center">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={20} className="text-yellow-500 fill-current" />
                  ))}
                </div>
                <span className="text-2xl font-heading font-bold text-foreground">
                  {overallStats?.averageRating}
                </span>
              </div>
              <p className="text-sm font-caption text-muted-foreground">
                Gemiddelde score uit {overallStats?.totalReviews?.toLocaleString('nl-NL')} reviews
              </p>
            </div>
          </div>
        </motion.div>

        {/* Auto-Scrolling Reviews Carousel */}
        <div 
          className="relative overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div 
            ref={scrollContainerRef}
            className="flex space-x-6 overflow-x-hidden"
            style={{ scrollBehavior: 'auto' }}
          >
            {duplicatedTestimonials?.map((testimonial, index) => (
              <motion.div
                key={`${testimonial?.id}-${index}`}
                className="flex-shrink-0 w-[350px] sm:w-[400px] bg-card border border-border rounded-xl p-6 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.12)] hover:shadow-[0_16px_40px_-10px_rgba(0,0,0,0.2)] transition-all transform perspective-1000"
                whileHover={{ 
                  scale: 1.03,
                  y: -8,
                  rotateX: 3,
                  boxShadow: "0 16px 40px -10px rgba(0, 0, 0, 0.2), 0 0 12px rgba(59, 130, 246, 0.08)",
                  transition: { duration: 0.3 }
                }}
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Customer Info */}
                <div className="flex items-center space-x-4 mb-4">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.alt}
                    className="w-14 h-14 rounded-full object-cover border-2 border-border"
                  />
                  <div className="flex-1">
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      {testimonial?.name}
                    </h3>
                    <p className="text-sm font-body text-muted-foreground">
                      {testimonial?.location}
                    </p>
                    <div className="flex items-center space-x-1 mt-1">
                      {[...Array(testimonial?.rating)]?.map((_, i) => (
                        <Icon key={i} name="Star" size={14} className="text-yellow-500 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Service Info */}
                <div className="mb-4 space-y-2">
                  <div className="flex items-center space-x-2">
                    <Icon name="Wrench" size={16} className="text-primary" />
                    <span className="text-sm font-body font-medium text-foreground">
                      {testimonial?.service}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Building" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-body text-muted-foreground">
                      Door: {testimonial?.provider}
                    </span>
                  </div>
                </div>

                {/* Testimonial Text */}
                <blockquote className="text-foreground font-body mb-4 italic text-sm leading-relaxed">
                  "{testimonial?.testimonial}"
                </blockquote>

                {/* Results Grid */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="text-center p-3 bg-success/10 rounded-lg shadow-[0_4px_14px_0_rgba(0,0,0,0.08)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.15)] hover:-translate-y-1 transition-all duration-300 transform">
                    <div className="text-base font-heading font-semibold text-success">
                      {testimonial?.savings}
                    </div>
                    <div className="text-xs font-caption text-muted-foreground">
                      Besparing
                    </div>
                  </div>
                  <div className="text-center p-3 bg-primary/10 rounded-lg shadow-[0_4px_14px_0_rgba(0,0,0,0.08)] hover:shadow-[0_6px_20px_rgba(59,130,246,0.15)] hover:-translate-y-1 transition-all duration-300 transform">
                    <div className="text-base font-heading font-semibold text-primary">
                      {testimonial?.installationTime}
                    </div>
                    <div className="text-xs font-caption text-muted-foreground">
                      Installatie
                    </div>
                  </div>
                  <div className="text-center p-3 bg-secondary/10 rounded-lg shadow-[0_4px_14px_0_rgba(0,0,0,0.08)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-300 transform">
                    <div className="text-base font-heading font-semibold text-secondary">
                      {testimonial?.energyReduction}
                    </div>
                    <div className="text-xs font-caption text-muted-foreground">
                      Minder
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Gradient Overlays */}
          <div className="absolute top-0 left-0 bottom-0 w-20 bg-gradient-to-r from-muted/30 to-transparent pointer-events-none" />
          <div className="absolute top-0 right-0 bottom-0 w-20 bg-gradient-to-l from-muted/30 to-transparent pointer-events-none" />
        </div>

        {/* Hover Instruction */}
        <div className="text-center mt-6">
          <p className="text-sm font-body text-muted-foreground italic">
            💡 Beweeg je cursor over de reviews om het scrollen te pauzeren
          </p>
        </div>

        {/* Filter CTA */}
        <div className="text-center mt-8">
          <p className="text-sm font-body text-muted-foreground mb-4">
            Filter reviews op oplossing of regio
          </p>
          <button className="inline-flex items-center space-x-2 px-6 py-3 bg-muted hover:bg-muted/80 rounded-lg transition-colors">
            <Icon name="Filter" size={16} className="text-muted-foreground" />
            <span className="font-body font-medium text-foreground">Lees alle ervaringen</span>
          </button>
        </div>

        {/* Stats Summary */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-3xl font-heading font-bold text-primary mb-2">
              780
            </div>
            <div className="text-sm font-body text-muted-foreground">
              Tevreden klanten
            </div>
          </div>
          <div>
            <div className="text-3xl font-heading font-bold text-success mb-2">
              €1,8 miljoen
            </div>
            <div className="text-sm font-body text-muted-foreground">
              Totale energiebesparing
            </div>
          </div>
          <div>
            <div className="text-3xl font-heading font-bold text-secondary mb-2">
              4.8/5
            </div>
            <div className="text-sm font-body text-muted-foreground">
              Gemiddelde waardering
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;